import { isNil, omitBy } from 'lodash'

import { Response } from '@marketplace-web/core-api/api-client-util'
import { findThumbnail } from '@marketplace-web/shared/utils'
import { ProductItemModel, transformItemBoxDto } from '@marketplace-web/item-box/product-item-data'

import {
  ClosetModel,
  ClosetUserModel,
  ClosetDto,
  ClosetPromotionUserDto,
  PromotedClosetsListResp,
  GetClosetPromotionArgs,
  ClosetPromotionItemDto,
} from '../types/closet-promotions'
import { ItemThumbnailSize } from '../constants/close-promotions'
import { transformCurrencyAmountDto } from './currency'

const calculateRating = (reputation: number): number => {
  return Math.round(reputation * 5 * 100) / 100
}

const getItemPhotosThumbnails = (
  photos: Array<{ thumbnails: Array<{ type: ItemThumbnailSize | string; url: string }> }>,
  size = ItemThumbnailSize.Medium,
) => {
  if (photos.length === 0) return undefined

  return photos
    .map(photo => photo.thumbnails.find(thumb => thumb.type === size)?.url)
    .filter((photoUrl): photoUrl is string => !!photoUrl)
}

const getItemThumbnail = (
  photos: Array<{
    is_main: boolean
    thumbnails: Array<{ type: ItemThumbnailSize | string; url: string }>
  }>,
  size = ItemThumbnailSize.Medium,
) => {
  const photo = photos?.find(({ is_main }) => is_main)

  return findThumbnail(photo, size)
}

const getDominantColor = (photos: Array<{ is_main: boolean; dominant_color_opaque: string }>) => {
  if (!photos.length) return null

  const photo = photos.find(({ is_main }) => is_main === true)

  if (!photo) return null

  return photo.dominant_color_opaque
}

export const getClosetPromotionsArgsToParams = (args: GetClosetPromotionArgs) => {
  const params = {
    per_page: args.perPage,
    screen_name: args.screenName,
    exclude_member_ids: args.excludedUserIds?.join(','),
    search_session_id: args.searchSessionId,
    include_self_preview: args.isPreview,
    ...args.catalogFilterParams?.filters,
    ...args.catalogFilterParams?.dynamicFilters,
  }

  return omitBy(params, isNil)
}

export const transformClosetUser = (user: ClosetPromotionUserDto): ClosetUserModel => {
  return {
    id: user.id,
    name: user.login,
    image: findThumbnail(user.photo),
    rating: calculateRating(user.feedback_reputation),
    ratingCount: user.feedback_count,
    isFollowed: user.is_favourite,
    itemCount: user.item_count,
  }
}

export const transformCloset = (closet: ClosetDto): ClosetModel => {
  const { items, user } = closet

  return {
    user: transformClosetUser(user),
    items,
    showBanner: false,
  }
}

export const transformClosets = (data: Array<ClosetDto>): Array<ClosetModel> =>
  data.map(transformCloset)

export const transformClosetPromotionsResponse = (response: Response<PromotedClosetsListResp>) =>
  transformClosets(response.promoted_closets)

const tranaformClosetPromotionItemsThumbnails = (
  item: ClosetPromotionItemDto,
): Array<string> | undefined => {
  if (item.photos && item.photos.length > 0) {
    return getItemPhotosThumbnails(item.photos, ItemThumbnailSize.Large)
  }

  if (!item.photo) return undefined

  return getItemPhotosThumbnails([item.photo], ItemThumbnailSize.Large)
}

const tranaformClosetPromotionItemThumbnail = (item: ClosetPromotionItemDto): string | null => {
  if (item.photos && item.photos.length > 0) {
    return getItemThumbnail(item.photos, ItemThumbnailSize.Large)
  }

  if (!item.photo) return null

  return getItemThumbnail([item.photo], ItemThumbnailSize.Large)
}

const transformClosetPromotionItemDominantColor = (item: ClosetPromotionItemDto): string | null => {
  if (item.photos && item.photos.length > 0) {
    return getDominantColor(item.photos)
  }

  if (!item.photo) return null

  return getDominantColor([item.photo])
}

export const transformClosetPromotionItemDtoToProductItem = (
  item: ClosetPromotionItemDto,
): ProductItemModel => ({
  id: item.id,
  title: item.title,
  user: { id: item.user_id, isBusiness: item.business_user },
  url: item.url,
  favouriteCount: item.favourite_count,
  price: transformCurrencyAmountDto(item.price),
  serviceFee: item.service_fee ? transformCurrencyAmountDto(item.service_fee) : null,
  totalItemPrice: item.total_item_price
    ? transformCurrencyAmountDto(item.total_item_price)
    : undefined,
  isFavourite: item.is_favourite,
  thumbnailUrl: tranaformClosetPromotionItemThumbnail(item),
  thumbnailUrls: tranaformClosetPromotionItemsThumbnails(item),
  dominantColor: transformClosetPromotionItemDominantColor(item),
  itemBox: item.item_box && transformItemBoxDto(item.item_box),
})
